// An inline-expanded subflow (the folder-tree icon on a flow node, `handleExpandNode` in NodeDefinitionEditor) is drawn by
// adding the subflow's own placements and edges to the edited flow's working graph: its nodes carry `parentNode` (the
// expanded node, or an enclosing expansion), and its edges run between those nodes. They are a view of another definition,
// not part of this one, so they must never be saved, connected to, or edited as this flow's placements.
//
// `parentNode` is the marker because nothing else sets it: the backend does not persist it, so a loaded graph never has it.

/** Whether a canvas node belongs to an inline expansion rather than to the edited flow. */
export const isInlineExpansionNode = (node: { readonly parentNode?: string | null }): boolean => node.parentNode != null;

/** Whether the canvas node with this id belongs to an inline expansion; false when no node has the id. */
export const isInlineExpansionNodeId = (
  nodes: readonly { readonly id: string; readonly parentNode?: string | null }[],
  id: string | null | undefined
): boolean => nodes.some((n) => n.id === id && isInlineExpansionNode(n));

/** Whether the canvas node with this id is currently expanded inline, i.e. an expansion hangs under it. */
export const isExpandedNodeId = (nodes: readonly { readonly parentNode?: string | null }[], id: string | null | undefined): boolean =>
  id != null && nodes.some((n) => n.parentNode === id);

/**
 * The graph as if every inline expansion were collapsed: expansion nodes dropped (nested expansions included, since they
 * carry `parentNode` too), and with them every edge that has an end on one.
 *
 * The expanded node itself stays, still typed `subflowGroup` and sized as a group; neither `type` nor `style` is persisted.
 */
export const collapseInlineExpansions = <
  N extends { readonly id: string; readonly parentNode?: string | null },
  E extends { readonly source: string; readonly target: string },
>(
  nodes: readonly N[],
  edges: readonly E[]
): { nodes: N[]; edges: E[] } => {
  const expansionIds = new Set(nodes.filter(isInlineExpansionNode).map((n) => n.id));
  return {
    nodes: nodes.filter((n) => !expansionIds.has(n.id)),
    edges: edges.filter((e) => !expansionIds.has(e.source) && !expansionIds.has(e.target)),
  };
};

/**
 * Whether an edge, drawn or about to be drawn, has an end on an expansion node. Such an edge belongs to the subflow (or would
 * name a node this flow does not save), so it can be neither drawn nor edited here.
 */
export const touchesInlineExpansion = (
  nodes: readonly { readonly id: string; readonly parentNode?: string | null }[],
  edge: { readonly source?: string | null; readonly target?: string | null }
): boolean => isInlineExpansionNodeId(nodes, edge.source) || isInlineExpansionNodeId(nodes, edge.target);
